import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  makeStyles, shorthands, tokens, Text, Badge, Button, SearchBox,
  Accordion, AccordionItem, AccordionHeader, AccordionPanel,
  MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { useApi } from '../api/context';
import { useAsync } from '../api/useAsync';
import { Page, PageHeader, Card, Loading, ErrorState, Empty } from '../components/common';
import { FrameworkGuide } from '../components/FrameworkGuide';
import { FrameworkCoverage } from '../components/FrameworkCoverage';
import { useCustomer } from '../context/CustomerContext';
import type { Control } from '../api/client';

const PAGE = 50;

const useStyles = makeStyles({
  bar: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  ref: { fontFamily: tokens.fontFamilyMonospace, fontWeight: tokens.fontWeightSemibold },
  head: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  body: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS },
  label: {
    color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold, textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  mapGrid: {
    display: 'grid', gridTemplateColumns: 'minmax(200px, 1fr) 2fr',
    columnGap: tokens.spacingHorizontalM, rowGap: tokens.spacingVerticalXXS,
  },
  mapWrap: {
    maxHeight: '320px', overflowY: 'auto', ...shorthands.padding(tokens.spacingVerticalS, 0),
  },
  back: { color: tokens.colorBrandForeground1, textDecorationLine: 'none', fontSize: tokens.fontSizeBase200 },
  pager: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalM },
});

/** Attributes are publisher specific, so read defensively rather than assuming. */
function str(attrs: Record<string, unknown> | null, key: string): string | null {
  const v = attrs?.[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/**
 * Friendly labels for the fields the importers store.
 *
 * Anything not listed still renders, under its raw key. Hiding an unrecognised
 * field would mean a new framework silently loses information nobody notices is
 * missing.
 */
const FIELD_LABELS: Record<string, string> = {
  assetType: 'Asset type',
  securityFunction: 'Security function',
  implementationGroups: 'Implementation groups',
  domain: 'Domain',
  question: 'Assessment question',
  weighting: 'Relative weighting',
  group: 'Section',
  applicability: 'Applies at classification',
  essentialEight: 'Essential Eight maturity',
  revision: 'Revision',
  updated: 'Last updated',
  oscalId: 'Publisher identifier',
  class: 'Control class',
  isSafeguard: 'Is a safeguard',
};

/**
 * Fields that describe the import rather than the control. Shown separately and
 * collapsed, because an analyst reading a control does not need the licence URL
 * every time, but somebody checking provenance does.
 */
const PROVENANCE = new Set(['attribution', 'licence', 'licenceUrl', 'commercialUse',
                            'mappingsUsableByEngine']);

/** Rendered on their own, so they are excluded from the generic list. */
const HANDLED = new Set(['mappings', 'question']);

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(String).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function mappingsOf(attrs: Record<string, unknown> | null): [string, string[]][] {
  const m = attrs?.['mappings'];
  if (!m || typeof m !== 'object') return [];
  return Object.entries(m as Record<string, unknown>)
    .map(([k, v]) => [k, Array.isArray(v) ? v.map(String) : [String(v)]] as [string, string[]])
    .sort((a, b) => a[0].localeCompare(b[0]));
}

function ControlRow({ control }: { control: Control }) {
  const styles = useStyles();
  const attrs = control.attributes;
  const group = str(attrs, 'group') ?? str(attrs, 'domain');
  const e8 = str(attrs, 'essentialEight');
  const mappings = mappingsOf(attrs);
  const usable = attrs?.['mappingsUsableByEngine'];

  const entries = Object.entries(attrs ?? {});
  const details = entries.filter(([k, v]) =>
    !PROVENANCE.has(k) && !HANDLED.has(k) &&
    v !== null && v !== undefined && v !== '' &&
    !(Array.isArray(v) && v.length === 0));
  const provenance = entries.filter(([k, v]) => PROVENANCE.has(k) && v !== null && v !== '');

  return (
    <AccordionItem value={control.controlRef}>
      <AccordionHeader>
        <div className={styles.head}>
          <span className={styles.ref}>{control.controlRef}</span>
          <Text>{control.title}</Text>
          {e8 && <Badge appearance="tint" color="brand" size="small">{e8}</Badge>}
          {control.implementationGroups.map((g) => (
            <Badge key={g} appearance="outline" size="small">{g}</Badge>
          ))}
          {mappings.length > 0 && (
            <Badge appearance="tint" color="informative" size="small">
              {mappings.length} mappings
            </Badge>
          )}
        </div>
      </AccordionHeader>

      <AccordionPanel>
        <div className={styles.body}>
          {group && <div className={styles.muted}>{group}</div>}

          {control.description && (
            <div>
              <div className={styles.label}>Statement</div>
              <Text size={300}>{control.description}</Text>
            </div>
          )}

          {str(attrs, 'question') && (
            <div>
              <div className={styles.label}>Assessment question</div>
              <Text size={300}>{str(attrs, 'question')}</Text>
            </div>
          )}

          {control.parentRef && (
            <div>
              <div className={styles.label}>Parent control</div>
              <span className={styles.ref}>{control.parentRef}</span>
            </div>
          )}

          {/* Everything else the publisher supplied. Driven by the data rather
              than a fixed list, so a framework with fields we have not seen
              before still shows them. */}
          {details.length > 0 && (
            <div>
              <div className={styles.label}>Details</div>
              <div className={styles.mapGrid}>
                {details.map(([key, value]) => (
                  <div key={key} style={{ display: 'contents' }}>
                    <span className={styles.muted}>{FIELD_LABELS[key] ?? key}</span>
                    <Text size={200}>{renderValue(value)}</Text>
                  </div>
                ))}
              </div>
            </div>
          )}

          {provenance.length > 0 && (
            <details>
              <summary className={styles.muted} style={{ cursor: 'pointer' }}>
                Source and licence
              </summary>
              <div className={styles.mapGrid} style={{ marginTop: '6px' }}>
                {provenance.map(([key, value]) => (
                  <div key={key} style={{ display: 'contents' }}>
                    <span className={styles.muted}>{FIELD_LABELS[key] ?? key}</span>
                    <Text size={200}>{renderValue(value)}</Text>
                  </div>
                ))}
              </div>
            </details>
          )}

          {mappings.length > 0 && (
            <div>
              <div className={styles.label}>Mapped to ({mappings.length})</div>
              {usable === false && (
                <MessageBar intent="info">
                  <MessageBarBody>
                    Shown for reference only. Using these mappings in the resolution engine
                    needs a commercial licence from the publisher, which has not been
                    recorded for this framework.
                  </MessageBarBody>
                </MessageBar>
              )}
              <div className={styles.mapWrap}>
                <div className={styles.mapGrid}>
                  {mappings.map(([framework, refs]) => (
                    <div key={framework} style={{ display: 'contents' }}>
                      <span className={styles.muted}>{framework}</span>
                      <span className={styles.ref}>{refs.join(', ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </AccordionPanel>
    </AccordionItem>
  );
}

export function FrameworkDetail() {
  const styles = useStyles();
  const api = useApi();
  const { frameworkId } = useParams<{ frameworkId: string }>();
  const [view, setView] = useState<'coverage' | 'catalogue'>('coverage');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [skip, setSkip] = useState(0);
  const [open, setOpen] = useState<string[]>([]);

  const { tenant } = useCustomer();
  const frameworks = useAsync(() => api.getFrameworks(), []);
  const page = useAsync(
    () => (frameworkId
      ? api.getControls(frameworkId, { search: applied, skip, take: PAGE })
      : Promise.resolve(null)),
    [frameworkId, applied, skip],
  );

  if (!frameworkId) return <ErrorState error="No framework specified." title="Not found" />;
  if (page.loading && !page.data) return <Loading label="Loading controls" />;
  if (page.error) return <ErrorState error={page.error} />;

  const framework = frameworks.data?.find((f) => f.frameworkId === frameworkId);
  const data = page.data;

  // An older API returned a bare array here rather than a page object. Detected
  // rather than treated as an empty result, because "no content loaded" is a
  // statement about the catalogue and this is a statement about the build.
  const staleApi = Array.isArray(data as unknown);

  const total = data?.total ?? 0;
  const shownTo = Math.min(skip + PAGE, total);

  // The framework list and this page disagreeing means something is wrong
  // between them, not that the framework is empty.
  const contradiction = !staleApi && total === 0 && !applied && framework?.contentLoaded === true;

  const runSearch = () => { setSkip(0); setApplied(search.trim()); };

  return (
    <Page>
      <Link to="/frameworks" className={styles.back}>Back to frameworks</Link>

      <div className={styles.head}>
        <FrameworkGuide frameworkId={frameworkId} name={framework?.name ?? frameworkId} />
        <Text className={styles.muted}>How to read this framework</Text>
      </div>

      <PageHeader
        title={framework?.name ?? frameworkId}
        description={framework
          ? `${framework.version ?? 'version not established'} · ${framework.publisher} · ${framework.licenceCode}`
          : undefined}
      />

      {/* Coverage first, catalogue second. A consultant opening a framework
          almost always wants to know what this customer's collection said about
          it, not to read the catalogue from the top. */}
      <div className={styles.bar}>
        <Button size="small" appearance={view === 'coverage' ? 'primary' : 'secondary'}
                onClick={() => setView('coverage')}>
          Coverage{tenant ? ` for ${tenant.displayName}` : ''}
        </Button>
        <Button size="small" appearance={view === 'catalogue' ? 'primary' : 'secondary'}
                onClick={() => setView('catalogue')}>
          Full catalogue
        </Button>
      </div>

      {view === 'coverage' ? (
        <Card>
          <FrameworkCoverage frameworkId={frameworkId} />
        </Card>
      ) : staleApi ? (
        <MessageBar intent="error">
          <MessageBarBody>
            The API returned a list where a page was expected, which means it is running an
            older build than this interface. Rebuild and restart the API, then reload.
          </MessageBarBody>
        </MessageBar>
      ) : contradiction ? (
        <MessageBar intent="error">
          <MessageBarBody>
            The frameworks page reports {framework?.controlCount ?? 0} controls loaded for
            this framework, but none came back. That is a disagreement between the list and
            this page rather than an empty catalogue, so it is worth investigating rather
            than re-importing.
          </MessageBarBody>
        </MessageBar>
      ) : total === 0 && !applied ? (
        <Empty>
          No content loaded for this framework. Fetch it from the publisher, or import a
          licensed copy, from the frameworks page.
        </Empty>
      ) : (
        <Card>
          <div className={styles.body}>
            <div className={styles.bar}>
              <SearchBox
                placeholder="Search reference, title or statement"
                value={search}
                onChange={(_, d) => setSearch(d.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                style={{ minWidth: '300px' }}
              />
              <Button appearance="primary" size="small" onClick={runSearch}>Search</Button>
              {applied && (
                <Button size="small" onClick={() => { setSearch(''); setApplied(''); setSkip(0); }}>
                  Clear
                </Button>
              )}
              <span className={styles.muted}>
                {total === 0 ? 'Nothing matches' : `${skip + 1} to ${shownTo} of ${total}`}
              </span>
            </div>

            {total === 0 ? (
              <Empty>No control matches that search.</Empty>
            ) : (
              <>
                <Accordion multiple collapsible openItems={open}
                           onToggle={(_, d) => setOpen(d.openItems as string[])}>
                  {(data?.controls ?? []).map((c) => (
                    <ControlRow key={c.controlRef} control={c} />
                  ))}
                </Accordion>

                {/* Paged rather than scrolled: the SCF is 1,534 controls, each
                    carrying several kilobytes of cross mappings. */}
                <div className={styles.pager}>
                  <Button size="small" disabled={skip === 0}
                          onClick={() => setSkip(Math.max(0, skip - PAGE))}>
                    Previous
                  </Button>
                  <Button size="small" disabled={shownTo >= total}
                          onClick={() => setSkip(skip + PAGE)}>
                    Next
                  </Button>
                  <span className={styles.muted}>{PAGE} per page</span>
                </div>
              </>
            )}
          </div>
        </Card>
      )}
    </Page>
  );
}
